import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Button, createStyles, keyframes} from '@mantine/core';
import {useId, useMediaQuery} from '@mantine/hooks';
import {captureException} from '@sentry/browser';
import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AIButton from 'llm/chat-library/ui/AIButton';
import {
  CHAT_INPUT_PLACEHOLDER_TEXT_STYLE,
  CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
} from 'llm/chat-library/ui/AIStyleConstants';
import {acceptRealtimeVoiceTerms, getRealtimeVoiceConsentStatus} from 'llm/data-capture/DataCaptureDAO';
import RealtimeVoiceConsentModal from 'llm/realtime-voice/RealtimeVoiceConsentModal';
import {VoiceUIController, VoiceUIState} from 'llm/realtime-voice/VoiceUIController';

interface VoiceInputStateConfig {
  visibleText: string;
  accessibleText: string;
  active: boolean;
  showStop: boolean;
  /** False until the session streams microphone audio, which mutes the border to gray. */
  showLiveBorder: boolean;
  showListeningHalo: boolean;
  showAgentWave: boolean;
}

const VOICE_INPUT_STATE_CONFIG: Record<VoiceUIState, VoiceInputStateConfig> = {
  [VoiceUIState.IDLE]: {
    visibleText: '',
    accessibleText: 'Voice chat ready',
    active: false,
    showStop: false,
    showLiveBorder: false,
    showListeningHalo: false,
    showAgentWave: false,
  },
  [VoiceUIState.CONNECTING]: {
    visibleText: 'Voice is starting…',
    accessibleText: 'Starting voice chat',
    active: true,
    showStop: true,
    showLiveBorder: false,
    showListeningHalo: false,
    showAgentWave: false,
  },
  [VoiceUIState.LISTENING]: {
    visibleText: 'Listening…',
    accessibleText: 'Listening',
    active: true,
    showStop: true,
    showLiveBorder: true,
    showListeningHalo: true,
    showAgentWave: false,
  },
  [VoiceUIState.AGENT_WORKING]: {
    visibleText: 'Agent working…',
    accessibleText: 'Agent working',
    active: true,
    showStop: true,
    showLiveBorder: true,
    showListeningHalo: false,
    showAgentWave: false,
  },
  [VoiceUIState.AGENT_SPEAKING]: {
    visibleText: 'Speaking…',
    accessibleText: 'Speaking',
    active: true,
    showStop: true,
    showLiveBorder: true,
    showListeningHalo: false,
    showAgentWave: true,
  },
};

export function isVoiceInputActive(state: VoiceUIState): boolean {
  return VOICE_INPUT_STATE_CONFIG[state].active;
}

const PENDING_TOOL_PRESENTATION: VoiceUIController = {
  state: VoiceUIState.AGENT_WORKING,
  isUserSpeaking: false,
  isAgentSpeaking: false,
  microphoneLevel: {getSnapshot: () => 0, subscribe: () => () => {}},
  start: () => Promise.resolve(),
  stop: () => Promise.resolve(),
};

/**
 * Shows pending tool work even after audio stops. This controller is for status and decoration only;
 * the start/stop button must use the real session controller, not this presentation override.
 */
export function useVoiceInputPresentation(
  controller: VoiceUIController | undefined,
  isToolProcessing: boolean
): VoiceUIController | undefined {
  return useMemo(() => {
    if (!isToolProcessing) {
      return controller;
    }
    return controller != null && isVoiceInputActive(controller.state)
      ? {...controller, state: VoiceUIState.AGENT_WORKING}
      : PENDING_TOOL_PRESENTATION;
  }, [controller, isToolProcessing]);
}

/** Footprint of the idle Voice control. The send control is padded to match it. */
export const VOICE_CONTROL_SIZE_PX = 40;

/** Dash length the comet travels in one lap, matching the rect's `pathLength`. */
const COMET_PATH_LENGTH = 100;
const COMET_LIVE_CYCLE_MS = 1_400;
/** The comet turns more slowly before the session is live, so connecting reads as spinning up. */
const COMET_PENDING_CYCLE_MS = 7_000;
const COMET_LIVE_SPEED = 1 / COMET_LIVE_CYCLE_MS;
const COMET_PENDING_SPEED = 1 / COMET_PENDING_CYCLE_MS;
/**
 * Exponential time constant for easing the comet between the two speeds. The comet reaches roughly
 * 95% of the target speed after three time constants, so the session going live reads as the border
 * accelerating rather than switching cycle length mid-lap.
 */
const COMET_SPEED_RAMP_MS = 400;
/** Roughly matches how long the speed ramp takes to settle, so colour and motion arrive together. */
const COMET_SATURATION_FADE_MS = 1_200;
const STOP_BAR_LIVE_CYCLE = '0.9s';
/** Slower pulse while connecting, so the equalizer matches the unhurried starting halo. */
const STOP_BAR_PENDING_CYCLE = '2.4s';

const VOICE_GRADIENT_BLUE = '#3C65FB';
const VOICE_GRADIENT_PURPLE = '#9775FA';
const VOICE_GRADIENT_PINK = '#E870C0';
const VOICE_BRAND_GRADIENT_STOPS: ReadonlyArray<{
  offset: string;
  classKey: 'gradientStop0' | 'gradientStop1' | 'gradientStop2';
}> = [
  {offset: '34%', classKey: 'gradientStop0'},
  {offset: '48%', classKey: 'gradientStop1'},
  {offset: '75%', classKey: 'gradientStop2'},
];

const exitAgentDot = keyframes({
  '0%': {opacity: 1, transform: 'translateY(0)'},
  '22%': {opacity: 1, transform: 'translateY(0)'},
  '60%': {opacity: 0, transform: 'translateY(28px)'},
  '100%': {opacity: 0, transform: 'translateY(40px)'},
});

const pulseBar = keyframes({
  '0%, 100%': {transform: 'scaleY(0.45)'},
  '50%': {transform: 'scaleY(1)'},
});

const AGENT_WAVE_CYCLE_MS = 3_000;
const AGENT_DOT_EXIT_MS = 850;
const AGENT_DOT_EXIT_STAGGER_MS = 35;
const AGENT_WAVE_EXIT_MS = AGENT_DOT_EXIT_MS + 4 * AGENT_DOT_EXIT_STAGGER_MS;
const LISTENING_HALO_INITIAL_LEVEL = 0.5;
const LISTENING_HALO_HIGH_BURST_PROBABILITY = 0.25;
const LISTENING_HALO_HIGH_BURST_MIN_LEVEL = 0.75;
const LISTENING_HALO_SIMULATION_INTERVAL_MIN_MS = 150;
const LISTENING_HALO_SIMULATION_INTERVAL_RANGE_MS = 200;
const LISTENING_HALO_MIN_OPACITY = 0.4;
const LISTENING_HALO_OPACITY_RANGE = 0.25;
const LISTENING_HALO_MAX_OPACITY = 0.9;
const LISTENING_HALO_MIN_SCALE = 0.75;
const LISTENING_HALO_SCALE_RANGE = 0.85;
const LISTENING_HALO_ENTER_MS = 320;
const LISTENING_HALO_EXIT_MS = 380;

function getAgentWaveOffset(timeMs: number, index: number): number {
  return Math.sin((timeMs / AGENT_WAVE_CYCLE_MS) * Math.PI * 2 - index * 0.9) * 10;
}

function getSettlingScale(progress: number): number {
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const smoothProgress = clampedProgress * clampedProgress * (3 - 2 * clampedProgress);
  return 1 - smoothProgress;
}

/**
 * Produces mostly low levels with occasional high bursts so the prototype halo reads as speech,
 * rather than a uniform pulse, when a real microphone level is unavailable.
 */
function getSimulatedListeningLevel(): number {
  const isHighBurst = Math.random() < LISTENING_HALO_HIGH_BURST_PROBABILITY;
  return isHighBurst
    ? LISTENING_HALO_HIGH_BURST_MIN_LEVEL + Math.random() * (1 - LISTENING_HALO_HIGH_BURST_MIN_LEVEL)
    : Math.random() * LISTENING_HALO_HIGH_BURST_MIN_LEVEL;
}

function getListeningHaloSimulationDelay(): number {
  return (
    LISTENING_HALO_SIMULATION_INTERVAL_MIN_MS + Math.random() * LISTENING_HALO_SIMULATION_INTERVAL_RANGE_MS
  );
}

function getListeningHaloOpacity(level: number): number {
  if (level >= LISTENING_HALO_HIGH_BURST_MIN_LEVEL) {
    return LISTENING_HALO_MAX_OPACITY;
  }
  return (
    LISTENING_HALO_MIN_OPACITY + (level / LISTENING_HALO_HIGH_BURST_MIN_LEVEL) * LISTENING_HALO_OPACITY_RANGE
  );
}

const useStyles = createStyles(() => ({
  activeRoot: {
    position: 'relative',
    isolation: 'isolate',
    boxSizing: 'border-box',
    display: 'flex',
    width: '100%',
    minHeight: 106,
    padding: 1,
    overflow: 'visible',
    borderRadius: 24,
    backgroundColor: Color.WHITE,
    boxShadow: '0 0 12px rgba(0, 13, 181, 0.10)',
  },
  activeBorder: {
    position: 'absolute',
    zIndex: 3,
    inset: 0,
    width: '100%',
    height: '100%',
    overflow: 'visible',
    pointerEvents: 'none',
    // Both states name the same filter function so the browser interpolates the saturation rather
    // than swapping it in one frame when the session goes live.
    filter: 'saturate(1)',
    transition: `filter ${COMET_SATURATION_FADE_MS}ms ease`,
  },
  pendingBorder: {
    filter: 'saturate(0.2)',
  },
  gradientStop0: {stopColor: VOICE_GRADIENT_BLUE},
  gradientStop1: {stopColor: VOICE_GRADIENT_PURPLE},
  gradientStop2: {stopColor: VOICE_GRADIENT_PINK},
  staticMotion: {animation: 'none !important', transition: 'none !important'},
  surface: {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 23,
    backgroundColor: Color.WHITE,
  },
  state: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    minHeight: 48,
    padding: '10px 16px',
  },
  visibleState: {
    position: 'relative',
    zIndex: 2,
    ...CHAT_INPUT_PLACEHOLDER_TEXT_STYLE,
    transition: 'opacity 220ms ease, transform 220ms ease',
  },
  listeningHalo: {
    position: 'absolute',
    zIndex: 0,
    top: -42,
    right: '6%',
    left: '6%',
    height: 86,
    opacity: 0,
    background: `linear-gradient(120deg, ${VOICE_GRADIENT_BLUE}, ${VOICE_GRADIENT_PURPLE}, ${VOICE_GRADIENT_PINK})`,
    borderRadius: '50%',
    filter: 'blur(34px)',
    transform: 'translate3d(0, 0, 0)',
    pointerEvents: 'none',
  },
  listeningHaloActive: {
    opacity: 0.9,
  },
  controls: {
    position: 'relative',
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 12px 11px',
  },
  leading: {display: 'flex', flex: 1, alignItems: 'center', minWidth: 0},
  trailing: {display: 'flex', alignItems: 'center', gap: 8},
  agentWave: {
    position: 'absolute',
    zIndex: 1,
    bottom: 'calc(100% + 28px)',
    left: '50%',
    display: 'flex',
    gap: 10,
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
  },
  agentDotStage: {
    opacity: 0,
    transform: 'translateY(64px)',
    transition: 'opacity 550ms cubic-bezier(.18,.8,.24,1), transform 550ms cubic-bezier(.18,.8,.24,1)',
  },
  agentDotStageActive: {opacity: 1, transform: 'translateY(0)'},
  agentDotStageExiting: {
    animation: `${exitAgentDot} ${AGENT_DOT_EXIT_MS}ms cubic-bezier(.4, 0, .2, 1) both`,
  },
  agentDot: {
    display: 'block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: Color.BLUE_400,
    willChange: 'transform',
  },
  stopButton: {
    '&&': {
      height: CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
      minHeight: CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
    },
    padding: '0 13px',
    border: 0,
    borderRadius: 12,
    backgroundColor: Color.BLUE_400,
    color: Color.WHITE,
    fontSize: 13,
    // Same named filter as `activeBorder` so Stop and the comet gain color together.
    filter: 'saturate(1)',
    transition: `filter ${COMET_SATURATION_FADE_MS}ms ease`,
    '&:hover': {backgroundColor: Color.BLUE_450},
  },
  connectingStopButton: {
    filter: 'saturate(0.2)',
  },
  stopIcon: {display: 'inline-flex', alignItems: 'center', gap: 2, marginRight: 7},
  stopBar: {
    display: 'block',
    width: 2,
    height: 12,
    borderRadius: 1,
    backgroundColor: 'currentColor',
    animation: `${pulseBar} ${STOP_BAR_LIVE_CYCLE} ease-in-out infinite`,
  },
  connectingStopBar: {
    animation: `${pulseBar} ${STOP_BAR_PENDING_CYCLE} ease-in-out infinite`,
  },
  idleButton: {
    width: VOICE_CONTROL_SIZE_PX,
    minWidth: VOICE_CONTROL_SIZE_PX,
    // Outrank the shared icon-only button size so the idle Voice footprint remains square.
    '&&': {
      height: VOICE_CONTROL_SIZE_PX,
      minHeight: VOICE_CONTROL_SIZE_PX,
    },
    padding: 0,
    borderRadius: 12,
  },
  screenReaderOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

export interface VoiceInputControlProps {
  controller: VoiceUIController;
  disabled?: boolean;
  leadingControl?: ReactNode;
  interruptControl?: ReactNode;
  onStartError?: (error: unknown) => void;
  onStopRequested?: () => void;
  reducedMotionOverride?: boolean;
}

function usePrefersReducedMotion(override?: boolean): boolean {
  const systemPreference = useMediaQuery('(prefers-reduced-motion: reduce)', undefined, {
    getInitialValueInEffect: false,
  });
  return override ?? systemPreference;
}

function ListeningHalo({
  active,
  reducedMotion,
  inputLevel,
}: {
  active: boolean;
  reducedMotion: boolean;
  /** Future microphone-level seam. Omit to use the prototype's simulated level. */
  inputLevel?: number;
}): JSX.Element {
  const {classes, cx} = useStyles();
  const [simulatedLevel, setSimulatedLevel] = useState(LISTENING_HALO_INITIAL_LEVEL);

  useEffect(() => {
    if (!active || reducedMotion || inputLevel != null) {
      return;
    }
    let timerId: number;
    const updateLevel = (): void => {
      setSimulatedLevel(getSimulatedListeningLevel());
      timerId = window.setTimeout(updateLevel, getListeningHaloSimulationDelay());
    };
    timerId = window.setTimeout(updateLevel, getListeningHaloSimulationDelay());
    return () => {
      window.clearTimeout(timerId);
    };
  }, [active, inputLevel, reducedMotion]);

  const level = Math.max(0, Math.min(1, inputLevel ?? simulatedLevel));
  const opacity = getListeningHaloOpacity(level);
  return (
    <span
      aria-hidden={true}
      className={cx(
        classes.listeningHalo,
        active && classes.listeningHaloActive,
        reducedMotion && classes.staticMotion
      )}
      data-active={active}
      data-motion={reducedMotion ? 'static' : 'animated'}
      {...getTestProps({component: 'VoiceInputControl', element: 'listeningHalo'})}
      style={{
        opacity: active ? opacity : 0,
        transform: reducedMotion
          ? 'none'
          : `translate3d(0, 0, 0) scaleY(${LISTENING_HALO_MIN_SCALE + level * LISTENING_HALO_SCALE_RANGE})`,
        transition: reducedMotion
          ? 'none'
          : `opacity ${
              active ? LISTENING_HALO_ENTER_MS : LISTENING_HALO_EXIT_MS
            }ms ease, transform ${LISTENING_HALO_ENTER_MS}ms cubic-bezier(.4, 0, .2, 1)`,
      }}
    />
  );
}

function LiveListeningHalo({
  controller,
  reducedMotion,
}: {
  controller: VoiceUIController;
  reducedMotion: boolean;
}): JSX.Element {
  const [inputLevel, setInputLevel] = useState(controller.microphoneLevel.getSnapshot);
  useEffect(
    () =>
      controller.microphoneLevel.subscribe(() => {
        setInputLevel(controller.microphoneLevel.getSnapshot());
      }),
    [controller.microphoneLevel]
  );
  return (
    <ListeningHalo
      active={controller.isUserSpeaking || inputLevel > 0.02}
      inputLevel={inputLevel}
      reducedMotion={reducedMotion}
    />
  );
}

function AgentWave({
  active,
  reducedMotion,
  sessionActive,
}: {
  active: boolean;
  reducedMotion: boolean;
  sessionActive: boolean;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  const dotRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const wasActiveRef = useRef(active);
  const [isExiting, setIsExiting] = useState(false);
  const shouldStartExit = !active && wasActiveRef.current;
  const showExit = isExiting || shouldStartExit;

  useLayoutEffect(() => {
    if (active) {
      setIsExiting(false);
    } else if (wasActiveRef.current) {
      setIsExiting(true);
    }
    wasActiveRef.current = active;
  }, [active]);

  useEffect(() => {
    if (!isExiting) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setIsExiting(false);
    }, AGENT_WAVE_EXIT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isExiting]);

  useEffect(() => {
    if (reducedMotion || (!active && !showExit)) {
      dotRefs.current.forEach((dot) => {
        if (dot != null) {
          dot.style.transform = 'translateY(0)';
        }
      });
      return;
    }

    let frameId: number;
    if (active) {
      const animate = (timeMs: number): void => {
        dotRefs.current.forEach((dot, index) => {
          if (dot != null) {
            dot.style.transform = `translateY(${getAgentWaveOffset(timeMs, index)}px)`;
          }
        });
        frameId = window.requestAnimationFrame(animate);
      };
      frameId = window.requestAnimationFrame(animate);
      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }

    let exitStartedAtMs: number | null = null;
    const settle = (timeMs: number): void => {
      exitStartedAtMs ??= timeMs;
      const elapsedMs = timeMs - exitStartedAtMs;
      dotRefs.current.forEach((dot, index) => {
        if (dot != null) {
          const settleProgress = (elapsedMs - index * AGENT_DOT_EXIT_STAGGER_MS) / AGENT_DOT_EXIT_MS;
          const offsetPx = getAgentWaveOffset(timeMs, index) * getSettlingScale(settleProgress);
          dot.style.transform = `translateY(${offsetPx}px)`;
        }
      });

      const finalExitMs = AGENT_DOT_EXIT_MS + (dotRefs.current.length - 1) * AGENT_DOT_EXIT_STAGGER_MS;
      if (elapsedMs < finalExitMs) {
        frameId = window.requestAnimationFrame(settle);
      } else {
        dotRefs.current.forEach((dot) => {
          if (dot != null) {
            dot.style.transform = 'translateY(0)';
          }
        });
      }
    };
    frameId = window.requestAnimationFrame(settle);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [active, reducedMotion, showExit]);

  if (!sessionActive && !active && !showExit) {
    return <></>;
  }

  return (
    <span
      aria-hidden={true}
      className={classes.agentWave}
      data-active={active}
      data-motion={reducedMotion ? 'static' : 'animated'}
      {...getTestProps({component: 'VoiceInputControl', element: 'agentWave'})}
    >
      {Array.from({length: 5}, (_, index) => {
        const stageStyle: CSSProperties = active
          ? {transitionDelay: `${index * 90}ms`}
          : {animationDelay: `${index * AGENT_DOT_EXIT_STAGGER_MS}ms`};
        return (
          <span
            data-amp-safe={true}
            {...getTestProps({component: 'VoiceInputControl', element: 'agentDotStage', key: String(index)})}
            className={cx(
              classes.agentDotStage,
              active && classes.agentDotStageActive,
              showExit && classes.agentDotStageExiting,
              reducedMotion && classes.staticMotion
            )}
            key={index}
            style={stageStyle}
          >
            <span
              {...getTestProps({component: 'VoiceInputControl', element: 'agentDot'})}
              className={classes.agentDot}
              ref={(element) => {
                dotRefs.current[index] = element;
              }}
            />
          </span>
        );
      })}
    </span>
  );
}

/**
 * Rotates the comet and its gradient from a single JS-held phase. CSS cannot transition
 * `animation-duration`, so driving the phase here lets the speed ease between states without the
 * comet jumping to a new position when the session goes live.
 */
function CometBorder({
  showLiveBorder,
  reducedMotion,
}: {
  showLiveBorder: boolean;
  reducedMotion: boolean;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  const gradientId = useId();
  const gradientRef = useRef<SVGLinearGradientElement>(null);
  const cometRef = useRef<SVGRectElement>(null);
  const phaseRef = useRef(0);
  const speedRef = useRef(showLiveBorder ? COMET_LIVE_SPEED : COMET_PENDING_SPEED);
  const showLiveBorderRef = useRef(showLiveBorder);
  showLiveBorderRef.current = showLiveBorder;

  useEffect(() => {
    if (reducedMotion) {
      return;
    }
    let frameId: number;
    let previousTimeMs: number | null = null;
    const advance = (timeMs: number): void => {
      const elapsedMs = previousTimeMs == null ? 0 : timeMs - previousTimeMs;
      previousTimeMs = timeMs;
      const targetSpeed = showLiveBorderRef.current ? COMET_LIVE_SPEED : COMET_PENDING_SPEED;
      speedRef.current += (targetSpeed - speedRef.current) * (1 - Math.exp(-elapsedMs / COMET_SPEED_RAMP_MS));
      phaseRef.current = (phaseRef.current + speedRef.current * elapsedMs) % 1;
      cometRef.current?.style.setProperty('stroke-dashoffset', String(-COMET_PATH_LENGTH * phaseRef.current));
      gradientRef.current?.setAttribute('gradientTransform', `rotate(${-360 * phaseRef.current} .5 .5)`);
      frameId = window.requestAnimationFrame(advance);
    };
    frameId = window.requestAnimationFrame(advance);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [reducedMotion]);

  return (
    <svg
      aria-hidden={true}
      className={cx(
        classes.activeBorder,
        !showLiveBorder && classes.pendingBorder,
        reducedMotion && classes.staticMotion
      )}
      data-motion={reducedMotion ? 'static' : 'animated'}
      {...getTestProps({component: 'VoiceInputControl', element: 'activeBorder'})}
    >
      <defs>
        <linearGradient
          {...getTestProps({component: 'VoiceInputControl', element: 'gradient'})}
          id={gradientId}
          ref={gradientRef}
          x1="0"
          x2="1"
          y1="0"
          y2="0"
        >
          {VOICE_BRAND_GRADIENT_STOPS.map((gradientStop, index) => (
            <stop
              className={classes[gradientStop.classKey]}
              data-amp-safe={true}
              {...getTestProps({
                component: 'VoiceInputControl',
                element: 'gradientStop',
                key: String(index),
              })}
              key={gradientStop.offset}
              offset={gradientStop.offset}
            />
          ))}
        </linearGradient>
      </defs>
      <rect
        {...getTestProps({component: 'VoiceInputControl', element: 'comet'})}
        fill="none"
        height="calc(100% - 1px)"
        pathLength={COMET_PATH_LENGTH}
        ref={cometRef}
        rx="24"
        stroke={`url(#${gradientId})`}
        strokeDasharray="32 68"
        strokeWidth="1"
        width="calc(100% - 1px)"
        x=".5"
        y=".5"
      />
    </svg>
  );
}

export function VoiceInputDecorations({
  controller,
  reducedMotionOverride,
}: Pick<VoiceInputControlProps, 'controller' | 'reducedMotionOverride'>): JSX.Element {
  const reducedMotion = usePrefersReducedMotion(reducedMotionOverride);
  const sessionActive = isVoiceInputActive(controller.state);
  const showLiveBorder = VOICE_INPUT_STATE_CONFIG[controller.state].showLiveBorder;

  return (
    <>
      {sessionActive && (
        <>
          <CometBorder reducedMotion={reducedMotion} showLiveBorder={showLiveBorder} />
          <LiveListeningHalo controller={controller} reducedMotion={reducedMotion} />
        </>
      )}
      <AgentWave
        active={controller.isAgentSpeaking}
        reducedMotion={reducedMotion}
        sessionActive={sessionActive}
      />
    </>
  );
}

export function VoiceInputStatus({
  controller,
  reducedMotionOverride,
}: Pick<VoiceInputControlProps, 'controller' | 'reducedMotionOverride'>): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  const reducedMotion = usePrefersReducedMotion(reducedMotionOverride);
  const config = VOICE_INPUT_STATE_CONFIG[controller.state];
  return (
    <div className={classes.state}>
      <span
        aria-hidden={true}
        className={cx(classes.visibleState, reducedMotion && classes.staticMotion)}
        data-transition={reducedMotion ? 'none' : 'animated'}
        {...getTestProps({component: 'VoiceInputControl', element: 'visibleState'})}
      >
        {config.visibleText}
      </span>
      <span aria-atomic={true} aria-live="polite" className={classes.screenReaderOnly} role="status">
        {config.accessibleText}
      </span>
    </div>
  );
}

export function VoiceToolProcessingStatus(): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const text = 'Voice is off — no microphone audio is being sent. Benchling is finishing your action…';
  return (
    <div className={classes.state}>
      <span
        aria-hidden={true}
        className={classes.visibleState}
        {...getTestProps({component: 'VoiceInputControl', element: 'visibleState'})}
      >
        {text}
      </span>
      <span aria-atomic={true} aria-live="polite" className={classes.screenReaderOnly} role="status">
        {text}
      </span>
    </div>
  );
}

export function VoiceInputAction({
  controller,
  disabled = false,
  onStartError,
  onStopRequested,
  reducedMotionOverride,
}: Omit<VoiceInputControlProps, 'leadingControl' | 'interruptControl'>): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  const stopButtonRef = useRef<HTMLButtonElement>(null);
  const reducedMotion = usePrefersReducedMotion(reducedMotionOverride);
  const config = VOICE_INPUT_STATE_CONFIG[controller.state];
  const [consentModalOpened, setConsentModalOpened] = useState(false);
  const consentCheckInFlightRef = useRef(false);
  const consentRequestGenerationRef = useRef(0);
  const pendingConsentGenerationRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const controllerRef = useRef(controller);
  const disabledRef = useRef(disabled);
  const [isCheckingConsent, setIsCheckingConsent] = useState(false);
  controllerRef.current = controller;
  disabledRef.current = disabled;
  useLayoutEffect(() => {
    isMountedRef.current = true;
    consentRequestGenerationRef.current += 1;
    consentCheckInFlightRef.current = false;
    pendingConsentGenerationRef.current = null;
    setConsentModalOpened(false);
    setIsCheckingConsent(false);
    return () => {
      isMountedRef.current = false;
      consentRequestGenerationRef.current += 1;
    };
  }, [disabled]);
  useEffect(() => {
    if (config.active) {
      stopButtonRef.current?.focus({preventScroll: true});
    }
  }, [config.active]);
  const isCurrentConsentRequest = useCallback(
    (generation: number): boolean =>
      isMountedRef.current &&
      generation === consentRequestGenerationRef.current &&
      !disabledRef.current &&
      controllerRef.current.state === VoiceUIState.IDLE,
    []
  );
  const startVoice = useCallback(
    (generation: number): void => {
      if (!isCurrentConsentRequest(generation)) {
        return;
      }
      controllerRef.current.start().catch((error: unknown) => onStartError?.(error));
    },
    [isCurrentConsentRequest, onStartError]
  );
  const handleStart = useCallback((): void => {
    if (consentCheckInFlightRef.current) {
      return;
    }
    const generation = consentRequestGenerationRef.current + 1;
    consentRequestGenerationRef.current = generation;
    consentCheckInFlightRef.current = true;
    setIsCheckingConsent(true);
    getRealtimeVoiceConsentStatus()
      .then(({hasCurrentAcceptance, isAcceptanceRequired}) => {
        if (!isCurrentConsentRequest(generation)) {
          return;
        }
        if (isAcceptanceRequired === false || hasCurrentAcceptance) {
          startVoice(generation);
        } else {
          pendingConsentGenerationRef.current = generation;
          setConsentModalOpened(true);
        }
      })
      .catch((error: unknown) => {
        onStartError?.(error);
      })
      .finally(() => {
        if (isMountedRef.current && generation === consentRequestGenerationRef.current) {
          consentCheckInFlightRef.current = false;
          setIsCheckingConsent(false);
        }
      });
  }, [isCurrentConsentRequest, onStartError, startVoice]);
  const handleCancelConsent = useCallback((): void => {
    consentRequestGenerationRef.current += 1;
    pendingConsentGenerationRef.current = null;
    setConsentModalOpened(false);
  }, []);
  const handleConsentGranted = useCallback((): void => {
    const generation = pendingConsentGenerationRef.current;
    if (generation == null || !isCurrentConsentRequest(generation)) {
      return;
    }
    pendingConsentGenerationRef.current = null;
    setConsentModalOpened(false);
    startVoice(generation);
  }, [isCurrentConsentRequest, startVoice]);
  const handleGrantConsent = useCallback(async (): Promise<void> => {
    const {hasCurrentAcceptance} = await acceptRealtimeVoiceTerms();
    if (!hasCurrentAcceptance) {
      throw new Error('The server did not confirm acceptance of the current Voice terms.');
    }
  }, []);
  const handleStop = useCallback(() => {
    onStopRequested?.();
    controller.stop().catch(captureException);
  }, [controller, onStopRequested]);

  if (!config.active) {
    return (
      <>
        <AIButton
          ariaLabel="Start voice chat"
          className={classes.idleButton}
          dataTestAttrs={{component: 'VoiceInputControl', element: 'startButton'}}
          disabled={disabled || consentModalOpened || isCheckingConsent}
          icon="waveform-lines"
          onClick={handleStart}
        />
        <RealtimeVoiceConsentModal
          onCancel={handleCancelConsent}
          onConsentGranted={handleConsentGranted}
          onGrantConsent={handleGrantConsent}
          opened={consentModalOpened}
        />
      </>
    );
  }

  return (
    <Button
      {...getTestProps({component: 'VoiceInputControl', element: 'stopButton'})}
      aria-label="Stop voice chat"
      className={cx(
        classes.stopButton,
        !config.showLiveBorder && classes.connectingStopButton,
        reducedMotion && classes.staticMotion
      )}
      compact={true}
      ref={stopButtonRef}
      leftIcon={
        <span aria-hidden={true} className={classes.stopIcon}>
          {[6, 12, 8, 14, 7].map((height, index) => (
            <span
              className={cx(
                classes.stopBar,
                !config.showLiveBorder && classes.connectingStopBar,
                reducedMotion && classes.staticMotion
              )}
              key={index}
              style={{animationDelay: `${index * 90}ms`, height}}
              {...getTestProps({component: 'VoiceInputControl', element: 'stopBar'})}
            />
          ))}
        </span>
      }
      onClick={handleStop}
    >
      Stop
    </Button>
  );
}

export default function VoiceInputControl(props: VoiceInputControlProps): JSX.Element {
  const {controller, leadingControl, interruptControl, reducedMotionOverride} = props;
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  if (!isVoiceInputActive(controller.state)) {
    return <VoiceInputAction {...props} />;
  }
  return (
    <div aria-label="Voice chat active" className={classes.activeRoot} role="group">
      <VoiceInputDecorations controller={controller} reducedMotionOverride={reducedMotionOverride} />
      <div className={classes.surface}>
        <VoiceInputStatus controller={controller} reducedMotionOverride={reducedMotionOverride} />
        <div className={classes.controls}>
          <div className={classes.leading}>{leadingControl}</div>
          <div className={classes.trailing}>
            {interruptControl}
            <VoiceInputAction {...props} />
          </div>
        </div>
      </div>
    </div>
  );
}
